<template>
    <Modal
        id="view-tracking-info-modal"
        v-model:open="open"
        title="View Tracking Info"
    >
        <div
            class="flex h-auto w-full flex-col items-start justify-start space-y-2"
        >
            <div
                class="flex h-10 w-full items-center justify-start space-x-1 rounded-md bg-gray-100 px-4"
            >
                <span class="font-bold text-gray-500">
                    {{ $t('event') }}:
                </span>
                <p>{{ extractedEvent }}</p>
            </div>
            <div
                class="flex h-10 w-full items-center justify-start space-x-1 rounded-md bg-gray-100 px-4"
            >
                <span class="font-bold text-gray-500"> {{ $t('User') }}: </span>
                <p>
                    {{ userName }}
                </p>
            </div>
            <div
                class="flex h-10 w-full items-center justify-start space-x-1 rounded-md bg-gray-100 px-4"
            >
                <span class="font-bold text-gray-500">
                    {{ $t('platform') }}:
                </span>
                <p>{{ data.platform_name }}</p>
            </div>
            <div
                v-for="item in Object.keys(data.data as object)"
                :key="item"
                class="flex h-auto w-full items-center justify-start space-x-1 rounded-md bg-gray-100 py-4 px-4"
            >
                <span class="whitespace-nowrap font-bold text-gray-500">
                    {{ extractedDataName(item) }}:
                </span>
                <a
                    v-if="renderLink(item)"
                    :href="extractItemPageUrl(item)"
                    target="_blank"
                    class="text-blue-500"
                >
                    {{ item.includes('_link') ? 'Doc Link' : data.data[item] }}
                    <fa
                        icon="fa-link"
                        class="text-blue-500"
                    />
                </a>
                <p v-else-if="item === 'changes'">
                    <VueJsonPretty
                        :data="data.data?.changes"
                        :deep="1"
                    />
                </p>
                <p v-else>
                    {{ data.data[item] }}
                </p>
            </div>
            <div
                class="flex h-10 w-full items-center justify-start space-x-1 rounded-md bg-gray-100 px-4"
            >
                <span class="font-bold text-gray-500">
                    {{ $t('created_at') }}
                </span>
                <p>{{ moment(data.created_at).format('DD.MM.yyyy HH:mm') }}</p>
            </div>
        </div>
    </Modal>
</template>

<script setup lang="ts">
import { Modal } from '@src/components'
import { LegalProfile, NaturalProfile, UserTrackingHistory } from '@src/types'
import { useVModels } from '@vueuse/core'
import moment from 'moment'
import { computed } from 'vue'
import VueJsonPretty from 'vue-json-pretty'
import 'vue-json-pretty/lib/styles.css'

const props = defineProps<{
    open: boolean
    data: UserTrackingHistory
}>()

const emit = defineEmits(['cancel', 'submit', 'update:open'])

const { open } = useVModels(props, emit)

// no-op

const extractedEvent = computed(() => {
    const eventValue = props.data?.event
    if (!eventValue) {
        return ''
    }
    return eventValue
        .split('_')
        .map((word: string) => {
            return word.charAt(0).toUpperCase() + word.slice(1)
        })
        .join(' ')
})

const extractedDataName = (name: string) => {
    if (name.includes('_')) {
        return name
            .split('_')
            .map((word: string) => {
                return word.charAt(0).toUpperCase() + word.slice(1)
            })
            .join(' ')
    }
    return name.charAt(0).toUpperCase() + name.slice(1)
}

const extractItemPageUrl = (item: string) => {
    const type = item.split('_')[0]
    if (type === 'campaign' && props.data?.data) {
        return `/campaigns/${props.data?.data[item]}`
    }
    if (type === 'asset' && props.data?.data) {
        return `/assets/${props.data?.data[item]}`
    }
    if (type === 'order' && props.data?.data) {
        return `/orders/${props.data?.data[item]}`
    }
    if (type === 'document' && props.data?.data) {
        return props.data?.data[item]
    }
    return '/'
}

const userName = computed(() => {
    const trackableType =
        props.data.trackable_type === 'App\\Models\\NaturalProfile'
            ? 'natural'
            : 'legal'
    if (trackableType === 'natural') {
        return (
            (props.data.trackable as NaturalProfile).first_name +
            ' ' +
            (props.data.trackable as NaturalProfile).last_name
        )
    } else if (trackableType === 'legal') {
        return (props.data.trackable as LegalProfile).company_name
    }
    return ''
})

const renderLink = (item: string) => {
    return (
        (item.includes('_id') && item.split('_')[0] !== 'document') ||
        item.includes('_link')
    )
}
</script>
